import cx from "classnames";
import { useEffect, useRef } from "react";
import { canv } from "@/canv/global";
import type { Group } from "@/group";
import { CloseIcon } from "@/icons";
import { EmptyBoardInfo } from "./EmptyBoardInfo";
import { NewVote } from "./NewVote";
import { PastVote } from "./PastVote";
import type { VoteWindowLayoutProps } from "./types";
import { VoteResults } from "./VoteResults";
import { VoteTabs } from "./VoteTabs";
import style from "./VoteWindow.module.css";

export function VoteWindowLayout({
    onClose,
    activeTab,
    onChangeTab,
    onStart,
    onMount,
    isShowResult,
    setShowResult,
    voteResult,
    selectResultItem,
    selectedResultItemUID,
}: VoteWindowLayoutProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (ref.current !== null) {
            onMount(ref.current);
        }
    }, [onMount]);

    const hasOneElement = canv.canvasController.items.some((group: Group) =>
        ["sticker", "shape", "image", "text", "textV2", "card"].includes(
            group.onlyChild()?.constructorName as string,
        ),
    );
    const hasPastVotes = canv.canvasController.pastVotes.length;
    const showEmptyBoardInfo = !hasOneElement && !hasPastVotes;

    return (
        <div className={style.voteWindow} ref={ref}>
            <VoteWindowHeader
                onClose={onClose}
                isShowResult={isShowResult}
                setShowResult={setShowResult}
            />
            <div className={`${style.voteWindowContainer} scrollable`}>
                {isShowResult && voteResult ? (
                    <VoteResults
                        resultsData={voteResult}
                        selectResultItem={selectResultItem}
                        selectedResultItemUID={selectedResultItemUID}
                    />
                ) : (
                    <>
                        {showEmptyBoardInfo && <EmptyBoardInfo />}
                        {!showEmptyBoardInfo && (
                            <>
                                <VoteTabs activeTab={activeTab} onSelect={onChangeTab} />
                                {activeTab === "new" && <NewVote onStart={onStart} />}
                                {activeTab === "past" && <PastVote setShowResult={setShowResult} />}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

type VoteWindowHeaderProps = Pick<
    VoteWindowLayoutProps,
    "onClose" | "isShowResult" | "setShowResult"
>;

export function VoteWindowHeader({ onClose, isShowResult, setShowResult }: VoteWindowHeaderProps) {
    return (
        <div className={style.voteWindowHeader}>
            {isShowResult ? (
                <div className={style.leftSideVoteHeader}>
                    <button
                        type="button"
                        className={style.closeVoteResultsButton}
                        onMouseDown={() => setShowResult(false)}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M18 10H2M2 10L10 18M2 10L10 2"
                                stroke="#2C2D32"
                                stroke-width="1.3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            ) : (
                <h2 className={style.voteWindowTitle}>Голосование</h2>
            )}
            <div className={style.voteWindowFilters}>
                <button
                    className={cx(style.voteWindowClose, style.voteWindowIconButton)}
                    type="button"
                    onClick={onClose}
                >
                    <CloseIcon className={style.closeIcon} />
                </button>
            </div>
        </div>
    );
}
